import { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./lib/auth";
import { initAnalytics, track } from "./lib/analytics";
import Layout from "./components/Layout";
import Landing from "./pages/Landing";
// Guide pages are the ad landing pages — eager, like the homepage (no second chunk fetch).
import GuidePage from "./pages/GuidePage";

// Route-level code splitting: everything except the homepage loads on demand so guide
// and content pages (the ad landing pages) never download admin/checkout/case code.
const lz = (loader, name) => lazy(() => loader().then((m) => ({ default: name ? m[name] : m.default })));
const Wizard = lz(() => import("./pages/Wizard"));
const StageStart = lz(() => import("./pages/StageStart"));
const GuideIndex = lz(() => import("./pages/GuideIndex"));
const FreeTools = lz(() => import("./pages/FreeTools"));
const GuideTools = lz(() => import("./pages/GuideTools"));
const Preview = lz(() => import("./pages/Preview"));
const Report = lz(() => import("./pages/Report"));
const Letter = lz(() => import("./pages/Letter"));
const Dashboard = lz(() => import("./pages/Dashboard"));
const KeepData = lz(() => import("./pages/KeepData"));
const Settings = lz(() => import("./pages/Settings"));
const CaseView = lz(() => import("./pages/CaseView"));
const Admin = lz(() => import("./pages/Admin"));
const AuthPage = lz(() => import("./pages/Auth"));
const ForgotPassword = lz(() => import("./pages/PasswordReset"), "ForgotPassword");
const ResetPassword = lz(() => import("./pages/PasswordReset"), "ResetPassword");
const SeoPage = lz(() => import("./pages/SeoPage"));
const BlogList = lz(() => import("./pages/Blog"), "BlogList");
const BlogPost = lz(() => import("./pages/Blog"), "BlogPost");
const Regulators = lz(() => import("./pages/Regulators"));
const NewsletterArchive = lz(() => import("./pages/NewsletterArchive"));
const About = lz(() => import("./pages/About"));
const Pricing = lz(() => import("./pages/StaticPages"), "Pricing");
const HowItWorks = lz(() => import("./pages/StaticPages"), "HowItWorks");
const Terms = lz(() => import("./pages/StaticPages"), "Terms");
const Privacy = lz(() => import("./pages/StaticPages"), "Privacy");
const Refund = lz(() => import("./pages/StaticPages"), "Refund");
const LegalChanges = lz(() => import("./pages/StaticPages"), "LegalChanges");
const PaymentSuccess = lz(() => import("./pages/PaymentResult"), "PaymentSuccess");
const PaymentCancel = lz(() => import("./pages/PaymentResult"), "PaymentCancel");
const MailStatus = lz(() => import("./pages/MailStatus"));
const CoverageCheck = lz(() => import("./pages/CoverageCheck"));
const Results = lz(() => import("./pages/Results"));
const ProviderStats = lz(() => import("./pages/ProviderStats"));
const MailMyAppeal = lz(() => import("./pages/MailMyAppeal"));
const VerifyEmail = lz(() => import("./pages/VerifyEmail"));
const MagicLink = lz(() => import("./pages/MagicLink"));
const AppCases = lz(() => import("./pages/AppCases"));
const Billing = lz(() => import("./pages/Billing"));
const AppDocuments = lz(() => import("./pages/AppDocuments"));
const AppHelp = lz(() => import("./pages/AppHelp"));
const NotFound = lz(() => import("./components/NotFound"));
import CookieConsent from "./components/CookieConsent";
import { useAuth } from "./lib/auth";

function PageTracker() {
  const location = useLocation();
  useEffect(() => { track("page_view", { page: location.pathname }); }, [location.pathname]);
  return null;
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  return children;
}

// Route changes must reset scroll — SPA navigation otherwise preserves the previous page's scroll depth.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  useEffect(() => { initAnalytics(); }, []);
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <PageTracker />
          <ScrollToTop />
          <Layout>
            <Suspense fallback={<div className="min-h-[60vh]" data-testid="route-loading" />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/check" element={<Wizard />} />
          <Route path="/start/:stage" element={<StageStart />} />
          <Route path="/guide" element={<GuideIndex />} />
          <Route path="/tools" element={<FreeTools />} />
          <Route path="/guide/tools" element={<GuideTools />} />
          <Route path="/guide/:slug" element={<GuidePage />} />
              <Route path="/claims/:claimId/preview" element={<Preview />} />
              <Route path="/claims/:claimId/report" element={<Report />} />
              <Route path="/claims/:claimId/letter" element={<Letter />} />
              <Route path="/app/cases" element={<RequireAuth><AppCases /></RequireAuth>} />
              <Route path="/app/settings/billing" element={<RequireAuth><Billing /></RequireAuth>} />
              <Route path="/app/settings" element={<RequireAuth><Settings /></RequireAuth>} />
              <Route path="/app/documents" element={<RequireAuth><AppDocuments /></RequireAuth>} />
              <Route path="/app/help" element={<RequireAuth><AppHelp /></RequireAuth>} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/cases/:claimId" element={<CaseView />} />
        <Route path="/keep-my-data" element={<KeepData />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/login" element={<AuthPage />} />
              <Route path="/login/magic" element={<MagicLink />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/refund" element={<Refund />} />
              <Route path="/legal/changes" element={<LegalChanges />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/state-regulators" element={<Regulators />} />
              <Route path="/newsletter/archive" element={<NewsletterArchive />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/mail/:orderId" element={<MailStatus />} />
              <Route path="/coverage-check" element={<CoverageCheck />} />
              <Route path="/coverage-check/:cid" element={<CoverageCheck />} />
              <Route path="/results" element={<Results />} />
              <Route path="/provider-stats" element={<ProviderStats />} />
              <Route path="/mail-my-appeal-letter" element={<MailMyAppeal />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/about" element={<About />} />
              <Route path="/:slug" element={<SeoPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </Layout>
          <CookieConsent />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
